/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { Text } from '../UNSAFE_Text';
import { TableFooterRendererContext } from '../UNSAFE_TableView';
import { classNames } from '../utils/UNSAFE_classNames';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { useId } from '../hooks/UNSAFE_useId';
import { TableFooterCellVariantOptions } from './themes/TableFooterCellStyles.css';
import { TableFooterCellRedwoodTheme } from './themes/redwood/TableFooterCellTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';

/**
 * Props for the TableFooterCell Component
 */
export type TableFooterCellProps<C> = {
  tableId: string;
  columnKey: C;
  columnIndex: number;
  isFinalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isColumnSelectable: boolean;
  isFocusRingVisible: boolean;
  isTabbable: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  isRtl: boolean;
  footerText?: string;
  stickyColumn?: 'start' | 'end' | 'none';
  renderer?: (context: TableFooterRendererContext<C>) => ComponentChildren;
};

function _defaultFooterCellRenderer<C>({ footerText }: TableFooterRendererContext<C>) {
  return footerText != null ? (
    <Text lineClamp={1} variant="primary" size="md">
      {footerText}
    </Text>
  ) : null;
}

/**
 * The internal component used to render a single cell in a TableFooterRow.
 */
export function TableFooterCell<C>({
  tableId,
  columnKey,
  columnIndex,
  isFinalColumnIndex,
  hasVerticalGridlines,
  isSelected,
  isPreviousSelected,
  isColumnSelectable,
  isFocusRingVisible,
  isTabbable,
  stickyColumn = 'none',
  footerText,
  renderer = _defaultFooterCellRenderer,
  isStickyStartEdge,
  isStickyEndEdge,
  isRtl
}: TableFooterCellProps<C>) {
  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle();

  const hasRenderer = renderer !== _defaultFooterCellRenderer;
  const { classes: themeClasses } = useComponentTheme<TableFooterCellVariantOptions>(
    TableFooterCellRedwoodTheme,
    {
      defaultRenderer: !hasRenderer ? 'isDefaultRenderer' : 'notDefaultRenderer',
      firstColumn: columnIndex === 0 ? 'isFirstColumn' : 'notFirstColumn',
      lastColumn: isFinalColumnIndex ? 'isLastColumn' : 'notLastColumn',
      borderStartSpacer:
        !(isPreviousSelected || isSelected) && (!hasVerticalGridlines || columnIndex === 0)
          ? 'isBorderStartSpacer'
          : 'notBorderStartSpacer',
      borderEndSpacer:
        isFinalColumnIndex && !isSelected ? 'isBorderEndSpacer' : 'notBorderEndSpacer',
      verticalGrid: hasVerticalGridlines ? 'isVerticalGrid' : 'notVerticalGrid',
      columnSelectable: isColumnSelectable ? 'isColumnSelectable' : 'notColumnSelectable',
      columnSelected: isSelected ? 'isColumnSelected' : 'notColumnSelected',
      previousColumnSelected: isPreviousSelected
        ? 'isPreviousColumnSelected'
        : 'notPreviousColumnSelected',
      stickyStartColumn: stickyColumn === 'start' ? 'isStickyStartColumn' : 'notStickyStartColumn',
      stickyEndColumn: stickyColumn === 'end' ? 'isStickyEndColumn' : 'notStickyEndColumn',
      stickyStartEdge: isStickyStartEdge ? 'isStickyStartEdge' : 'notStickyStartEdge',
      stickyEndEdge: isStickyEndEdge ? 'isStickyEndEdge' : 'notStickyEndEdge',
      focusRingVisible: isFocusRingVisible ? 'isFocusRingVisible' : 'notFocusRingVisible',
      active: applyActiveStyle ? 'isActive' : 'notActive',
      hover: applyHoverStyle ? 'isHover' : 'notHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      rtl: isRtl ? 'isRtl' : 'notRtl'
    }
  );

  const cellClasses = classNames([themeClasses]);

  const footerCellRendererProps = { key: columnKey, footerText: footerText };

  // TODO - make sure JAWS reads the aria-rowindex
  // correctly - may need another div if not
  return (
    <td
      aria-colindex={columnIndex + 1}
      {...(isSelected
        ? { 'aria-selected': true }
        : isColumnSelectable
        ? { 'aria-selected': false }
        : {})}
      class={cellClasses}
      id={useId()}
      tabIndex={-1}
      role={'gridcell'}
      data-oj-cell-type={'footer'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-footer-cell={tableId}
      {...(typeof columnKey === 'number' && { 'data-oj-column-key-type': 'number' })}
      {...(isColumnSelectable && interactionProps)}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        {renderer(footerCellRendererProps)}
      </TabbableModeContext.Provider>
    </td>
  );
}
